/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TimelineItemProps {
  id: string;
  timestampTypeId: string;
  required: boolean;
  sequence: number;
  timelineId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TimelineItemRecord extends Record<TimelineItemProps>({
  id: null as any,
  timestampTypeId: null as any,
  required: null as any,
  sequence: null as any,
  timelineId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    timestampTypeId: string;
    required: boolean;
    sequence: number;
    timelineId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TimelineItemRecord {
    if (json == null) {
      throw new Error('TimelineItemRecord.fromJS: parameter cannot be null');
    }
    return new TimelineItemRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TimelineItemJson.id',
      ),
      timestampTypeId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'timestampTypeId', '00000000-0000-0000-0000-000000000000'),
        'TimelineItemJson.timestampTypeId',
      ),
      required: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'required', false),
        'TimelineItemJson.required',
      ),
      sequence: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'sequence', 0),
        'TimelineItemJson.sequence',
      ),
      timelineId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'timelineId', '00000000-0000-0000-0000-000000000000'),
        'TimelineItemJson.timelineId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    timestampTypeId: string;
    required: boolean;
    sequence: number;
    timelineId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TimelineItemRecord, Error[]] {
    if (json == null) {
      throw new Error('TimelineItemRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TimelineItemRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TimelineItemJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timestampTypeId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'timestampTypeId', '00000000-0000-0000-0000-000000000000'),
            'TimelineItemJson.timestampTypeId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      required: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'required', false),
            'TimelineItemJson.required',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sequence: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'sequence', 0),
            'TimelineItemJson.sequence',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timelineId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'timelineId', '00000000-0000-0000-0000-000000000000'),
            'TimelineItemJson.timelineId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TimelineItemProps) {
    super(props);
    if (props == null) {
      throw new Error('TimelineItemRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TimelineItemViewModelBase implements TimelineItemProps {
  protected readonly _props: TimelineItemProps;
  private _json?: TimelineItemProps;

  get id(): string {
    return this._props.id;
  }
  get timestampTypeId(): string {
    return this._props.timestampTypeId;
  }
  get required(): boolean {
    return this._props.required;
  }
  get sequence(): number {
    return this._props.sequence;
  }
  get timelineId(): string {
    return this._props.timelineId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TimelineItemProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        timestampTypeId: this._props.timestampTypeId,
        required: this._props.required,
        sequence: this._props.sequence,
        timelineId: this._props.timelineId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTimelineItemEntities = (state: AppState) =>
  state.entityStores.timelineItem.entities;

export const selectTimelineItemById = (id: string) => (state: AppState) =>
  state.entityStores.timelineItem.entities.get(id);

export const selectTimelineItemMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.timelineItem.meta.get(id);

export const selectTimelineItemAndMetadataById = (id: string) =>
  createSelector(
    selectTimelineItemById(id),
    selectTimelineItemMetadataById(id),
    (timelineItem, meta) => ({ timelineItem, meta }),
  );

export function selectTimelineItemEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTimelineItemEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TimelineItemRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTimelineItemEntitiesByTimestampTypeId(timestampTypeId: string) {
  return createSelector(selectAllTimelineItemEntities, (entities) =>
    entities.filter((e) => e.timestampTypeId === timestampTypeId).valueSeq(),
  );
}

export function selectTimelineItemEntitiesByRequired(required: boolean) {
  return createSelector(selectAllTimelineItemEntities, (entities) =>
    entities.filter((e) => e.required === required).valueSeq(),
  );
}

export function selectTimelineItemEntitiesBySequence(sequence: number) {
  return createSelector(selectAllTimelineItemEntities, (entities) =>
    entities.filter((e) => e.sequence === sequence).valueSeq(),
  );
}

export function selectTimelineItemEntitiesByTimelineId(timelineId: string) {
  return createSelector(selectAllTimelineItemEntities, (entities) =>
    entities.filter((e) => e.timelineId === timelineId).valueSeq(),
  );
}
//#endregion Selectors
